import { describe_error } from '../../utils/ErrorDescriber';
import React, { useState, useEffect, useRef } from 'react';
import { Row, Col, Form, Button, Spinner, Alert } from 'react-bootstrap';
import axios from 'axios';

import Constants from '../../Constants';
import { useAuth } from '../../AuthContext';
import { get_user_error_message } from '../../utils/ErrorMessages';
import { get_status_label, format_date } from './UserFormat';
import InviteUserModal from './InviteUserModal';

// Users to a page.
const c_UsersPerPage = 20;

// Shown when a status that ends the user's sessions was saved but the auth service did not end them.
export const c_SessionsNotEndedText = 'The status was changed, but the user\'s sessions could not be ended: the auth service did not answer.';

// Shown when an invited user was created but the invite email did not go out.
const c_InviteNotSentText = 'The user was created, but the invite email could not be sent. Use Resend Invite on the user\'s page to send it.';

// The columns, in order: the backend's sort key of each, its heading and its column class. The sort runs on the
// backend, since the users come a page at a time.
const c_Columns = [
	{ key: 'email', label: 'Email', class_name: 'users-row-email-col' },
	{ key: 'name', label: 'Name', class_name: 'users-row-name-col' },
	{ key: 'status', label: 'Status', class_name: 'users-row-status-col' },
	{ key: 'email_verified', label: 'Verified', class_name: 'users-row-verified-col' },
	{ key: 'lastlogin', label: 'Last login', class_name: 'users-row-date-col' },
	{ key: 'createdAt', label: 'Signed up', class_name: 'users-row-date-col' }
];

// The sort keys that compare as text, from A to Z on the first click; the others newest or highest first.
const c_TextSortKeys = ['email', 'name', 'status'];

// The sort until a heading is clicked: newest first.
const c_DefaultSortKey = 'createdAt';
const c_DefaultSortDescending = true;

// The users, for the superadmin page: a page at a time in the style of the site list (a header row, then a card per
// user), searched by email or name and filtered by status. A click on a heading sorts the users by its column, and
// a second click on the same heading reverses the order. Each row's status is a dropdown that changes it on the
// spot; a click anywhere else on a row calls on_open with the user.
function UsersTable({on_open}){
	const { token } = useAuth();

	const [users, set_users] = useState(null);
	const [total, set_total] = useState(0);
	const [statuses, set_statuses] = useState([]);
	const [page, set_page] = useState(0);

	const [sort_key, set_sort_key] = useState(c_DefaultSortKey);
	const [sort_descending, set_sort_descending] = useState(c_DefaultSortDescending);

	// The search box's text, and the search the list was last loaded with: a search runs on Enter or the button.
	const [search_text, set_search_text] = useState('');
	const [search, set_search] = useState('');
	const [status_filter, set_status_filter] = useState('');

	const [loading, set_loading] = useState(false);
	const [error, set_error] = useState('');

	// The user whose status is being saved, so their dropdown is disabled meanwhile.
	const [saving_user_id, set_saving_user_id] = useState(null);

	// Whether the invite modal is open.
	const [inviting, set_inviting] = useState(false);

	const mounted_ref = useRef(true);

	const get_headers = ()=>{
		return { Authorization: 'Bearer ' + token };
	};

	const load_users = async()=>{
		set_loading(true);
		set_error('');

		try {
			const params = { page, per_page: c_UsersPerPage, sort: sort_key, order: (sort_descending ? 'desc' : 'asc') };
			if(search){
				params.search = search;
			}

			if(status_filter){
				params.status = status_filter;
			}

			const response = await axios.get(Constants.SERVER_URL + '/api/superadmin/users', { params, headers: get_headers() });
			if(!mounted_ref.current){
				return;
			}

			set_users(response.data.users);
			set_total(response.data.total);
			set_statuses(response.data.statuses);
		}
		catch (load_error) {
			console.log('UsersTable/load_users - ERROR: ', describe_error(load_error));
			if(mounted_ref.current){
				set_error(get_user_error_message(load_error, 'The users could not be loaded.'));
				set_users([]);
			}
		}

		if(mounted_ref.current){
			set_loading(false);
		}
	};

	useEffect(()=>{
		mounted_ref.current = true;
		load_users();

		return ()=>{
			mounted_ref.current = false;
		};
	}, [token, page, search, status_filter, sort_key, sort_descending]);

	// The pages: another page opens as soon as a page is full (c_UsersPerPage users or more), so with exactly a page's
	// worth of users Next is live and the page after is empty.
	const num_pages = Math.floor(total / c_UsersPerPage) + 1;

	const run_search = (event)=>{
		event.preventDefault();
		set_page(0);
		set_search(search_text.trim());
	};

	const on_status_filter_change = (event)=>{
		set_page(0);
		set_status_filter(event.target.value);
	};

	const on_header_click = (key)=>{
		set_page(0);
		if(key === sort_key){
			set_sort_descending(!sort_descending);
			return;
		}

		set_sort_key(key);
		set_sort_descending(!c_TextSortKeys.includes(key));
	};

	const change_status = async(user, status)=>{
		if(status === user.status){
			return;
		}

		set_saving_user_id(user.id);
		set_error('');

		try {
			const response = await axios.put(Constants.SERVER_URL + '/api/superadmin/user/' + user.id + '/status', { status }, { headers: get_headers() });
			if(!mounted_ref.current){
				return;
			}

			const updated_user = response.data.user;
			set_users(users.map((listed_user)=>{ return (listed_user.id === updated_user.id) ? updated_user : listed_user; }));
			if(response.data.tokens_revoked === false){
				set_error(c_SessionsNotEndedText);
			}
		}
		catch (save_error) {
			console.log('UsersTable/change_status - ERROR: ', describe_error(save_error));
			if(mounted_ref.current){
				set_error(get_user_error_message(save_error, 'The status could not be changed.'));
			}
		}

		if(mounted_ref.current){
			set_saving_user_id(null);
		}
	};

	// Sends the invite from the modal, then opens the new user as a row click does. Throws an Error carrying the
	// message to show, so the modal stays open with it. When the user was created but the email did not go out,
	// the table stays on screen with its error saying so, reloaded so the new user is listed and can be opened for
	// Resend Invite; opening the user straight away would navigate past the message.
	const invite_user = async(invite_data)=>{
		var response;
		try {
			response = await axios.post(Constants.SERVER_URL + '/api/superadmin/invite_user', invite_data, { headers: get_headers() });
		}
		catch (error) {
			console.log('UsersTable/invite_user - ERROR: ', describe_error(error));
			throw new Error(get_user_error_message(error, 'The invite could not be sent.'));
		}

		if(!mounted_ref.current){
			return;
		}

		set_inviting(false);
		if(response.data.invite_sent === false){
			set_error(c_InviteNotSentText);
			await load_users();
			return;
		}

		on_open(response.data.user);
	};

	const render_toolbar = ()=>{
		return (
			<Form className="users-toolbar" onSubmit={run_search}>
				<Row className="align-items-center">
					<Col xs={12} md={6} className="users-toolbar-search">
						<Form.Control
							type="text"
							placeholder="Search by email or name"
							value={search_text}
							onChange={(event)=>{ set_search_text(event.target.value); }}/>
						<Button type="submit" variant="primary" className="users-search-button" disabled={loading}>
							Search
						</Button>
					</Col>
					<Col xs={12} md={3}>
						<Form.Select value={status_filter} onChange={on_status_filter_change}>
							<option value="">All statuses</option>
							{statuses.map((status)=>{
								return <option key={status} value={status}>{get_status_label(status)}</option>;
							})}
						</Form.Select>
					</Col>
					<Col xs={12} md={3} className="users-toolbar-count">
						<span className="users-toolbar-count-text">
							{loading ? <Spinner animation="border" size="sm"/> : (total + ' user' + ((total === 1) ? '' : 's'))}
						</span>
						<Button type="button" variant="primary" className="users-invite-button" onClick={()=>{ set_inviting(true); }}>
							Invite User
						</Button>
					</Col>
				</Row>
			</Form>
		);
	};

	// A heading's text, with an arrow on the column the users are sorted by.
	const render_header_text = (column)=>{
		if(sort_key !== column.key){
			return column.label;
		}

		return column.label + (sort_descending ? ' ▼' : ' ▲');
	};

	const render_header = ()=>{
		return (
			<Row className="sites-header-row users-header-row">
				{c_Columns.map((column)=>{
					return (
						<Col
							key={column.key}
							className={column.class_name + ' sites-header-sortable'}
							onClick={()=>{ on_header_click(column.key); }}>
							{render_header_text(column)}
						</Col>
					);
				})}
			</Row>
		);
	};

	// The status dropdown: a click on it must not open the user.
	const render_status_select = (user)=>{
		return (
			<Form.Select
				className="users-status-select"
				size="sm"
				value={user.status ? user.status : ''}
				disabled={saving_user_id === user.id}
				onClick={(event)=>{ event.stopPropagation(); }}
				onChange={(event)=>{ change_status(user, event.target.value); }}>
				{statuses.map((status)=>{
					return <option key={status} value={status}>{get_status_label(status)}</option>;
				})}
			</Form.Select>
		);
	};

	const render_row = (user)=>{
		const name = [user.firstname, user.lastname].filter((part)=>{ return part; }).join(' ');
		return (
			<Row key={user.id} className="sites-row users-row" onClick={()=>{ on_open(user); }}>
				<Col className="users-row-email-col" title={user.email}>{user.email}</Col>
				<Col className="users-row-name-col" title={name}>{name}</Col>
				<Col className="users-row-status-col">{render_status_select(user)}</Col>
				<Col className="users-row-verified-col">{user.email_verified ? 'Yes' : 'No'}</Col>
				<Col className="users-row-date-col">{format_date(user.lastlogin)}</Col>
				<Col className="users-row-date-col">{format_date(user.createdAt)}</Col>
			</Row>
		);
	};

	const render_rows = ()=>{
		if(users === null){
			return (
				<div className="superadmin-loading">
					<Spinner animation="border" size="sm"/>
				</div>
			);
		}

		if(users.length === 0){
			return <p className="superadmin-empty">{(page > 0) ? 'No more users.' : 'No users found.'}</p>;
		}

		return (
			<div className="sites-rows users-rows">
				{render_header()}
				{users.map(render_row)}
			</div>
		);
	};

	// Previous and Next, the app's primary buttons, with the page count between them.
	const render_pagination = ()=>{
		if((users === null) || (total === 0)){
			return null;
		}

		return (
			<div className="users-pagination">
				<Button variant="primary" disabled={loading || (page === 0)} onClick={()=>{ set_page(page - 1); }}>
					Previous
				</Button>
				<span className="users-pagination-text">{'Page ' + (page + 1) + ' of ' + num_pages}</span>
				<Button variant="primary" disabled={loading || (page >= num_pages - 1)} onClick={()=>{ set_page(page + 1); }}>
					Next
				</Button>
			</div>
		);
	};

	const render_invite_modal = ()=>{
		if(!inviting){
			return null;
		}

		return (
			<InviteUserModal
				on_invite={invite_user}
				on_close={()=>{ set_inviting(false); }}/>
		);
	};

	return (
		<div className="users-table-container">
			{render_toolbar()}
			{error && <Alert variant="danger">{error}</Alert>}
			{render_rows()}
			{render_pagination()}
			{render_invite_modal()}
		</div>
	);
}

export default UsersTable;
